import { AppConfig } from "@/common/appConfig";
import type { Breadcrumb } from "@/common/types/types";
import { FeedbackHost } from "@/components/ui/feedback/FeedbackHost";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { OfflineIndicator } from "@/components/ui/offline-indicator/OfflineIndicator";
import { OfflineRecordingCenter } from "@/components/ui/offline-recording/OfflineRecordingCenter";
import { Toaster } from "@/components/ui/toast/toaster";
import { ToasterV3 } from "@/components/ui/toast/toaster-v3";
import { WhatsNewHost } from "@/components/ui/whats-new/WhatsNewHost";
import { V3Shell, type V3TopBarMode } from "../v3/V3Shell";
import { AppSidebar } from "./AppSidebar";
import { Container } from "./container";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { Navbar, type PageHeaderBar } from "./navbar";

interface LayoutProps {
  children: React.ReactNode;
  navbarTitle: string;
  breadcrumbs?: Breadcrumb[];
  icon?: React.ReactNode;
  iconHandler?: () => void;
  isLoading?: boolean;
  // Redesigned pages: count and primary action in the top bar.
  pageHeader?: PageHeaderBar;
  // v3.2 shell only: "none" when the page draws its own top bar (the visit
  // workspace), "desktop" when it has its own header on phones.
  topBar?: V3TopBarMode;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  navbarTitle,
  breadcrumbs = [],
  icon,
  iconHandler,
  isLoading = false,
  pageHeader,
  topBar,
}) => {
  if (AppConfig.enableAppRedesign) {
    return (
      <>
        <V3Shell
          breadcrumbs={breadcrumbs}
          isLoading={isLoading}
          pageHeader={pageHeader}
          title={navbarTitle}
          topBar={topBar}
        >
          <ImpersonationBanner />
          {children}
        </V3Shell>
        <OfflineRecordingCenter />
        {AppConfig.enableWhatsNew && <WhatsNewHost />}
        <FeedbackHost />
        <OfflineIndicator />
        <ToasterV3 />
      </>
    );
  }
  return (
    // data-testid: screenshot checks refuse the classic shell for v3.2 shots.
    <div
      className="layout flex h-screen w-full supports-[height:100dvh]:h-dvh"
      data-testid="classic-shell"
    >
      <AppSidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Navbar
          breadcrumbs={breadcrumbs}
          icon={icon}
          iconHandler={iconHandler}
          pageHeader={pageHeader}
          title={navbarTitle}
        />
        <ImpersonationBanner />
        {isLoading ? (
          <div className="flex w-full flex-1">
            <LoadingSpinner className="m-auto" size={8} />
          </div>
        ) : (
          <Container>{children}</Container>
        )}
        <OfflineRecordingCenter />
        {AppConfig.enableWhatsNew && <WhatsNewHost />}
        <FeedbackHost />
        <Toaster />
      </div>
    </div>
  );
};
